import React from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const CurrencyInput = ({ label, value, onChange, disabled, className, isReadOnly }) => (
    <div className={`space-y-1.5 ${className} min-w-0`}>
        <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">{label}</Label>
        <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">R$</span>
            <Input
                type="number"
                step="0.01"
                value={value}
                onChange={onChange}
                disabled={disabled}
                readOnly={isReadOnly}
                className={`pl-8 bg-white border-slate-200 focus:bg-white transition-colors ${isReadOnly ? "bg-slate-50 border-blue-100" : ""}`}
            />
        </div>
    </div>
);

export function DealForm({ data, onChange, disabled, showTipoAceite = false }) {
    const handleChange = (field, value) => {
        onChange({ ...data, [field]: value });
    };

    return (
        <div className="space-y-6">
            {/* Status Override (edit mode only) */}
            {showTipoAceite && (
                <div className="bg-amber-50/60 p-4 rounded-xl border border-amber-100 space-y-2">
                    <h5 className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Status de Assinatura</h5>
                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-slate-600">Status</Label>
                        <Select
                            value={data.tipo_aceite ?? "pendente"}
                            onValueChange={val => handleChange('tipo_aceite', val === "pendente" ? null : val)}
                            disabled={disabled}
                        >
                            <SelectTrigger className="bg-white border-slate-200">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="pendente">Pendente</SelectItem>
                                <SelectItem value="acordo">Aceitou o Acordo</SelectItem>
                                <SelectItem value="cessao">Aceitou a Cessão</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            {/* Core Inputs Section */}
            <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 space-y-4">
                <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Dados de Origem e Deságio</h5>
                <div className="grid grid-cols-2 gap-4">
                    <CurrencyInput
                        label="Valor Crédito"
                        value={data.valor_credito}
                        onChange={e => handleChange('valor_credito', e.target.value)}
                        disabled={disabled}
                    />
                    <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">% Deságio</Label>
                        <div className="relative">
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">%</span>
                            <Input
                                type="number"
                                value={data.percentual_desagio}
                                onChange={e => handleChange('percentual_desagio', e.target.value)}
                                disabled={disabled}
                                className="pr-8 bg-white border-slate-200 focus:bg-white transition-colors"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <CurrencyInput
                        label="Honorários Sindicato"
                        value={data.valor_sindicato}
                        onChange={e => handleChange('valor_sindicato', e.target.value)}
                        disabled={disabled}
                    />
                    <CurrencyInput
                        label="Honorários Contador"
                        value={data.valor_contador}
                        onChange={e => handleChange('valor_contador', e.target.value)}
                        disabled={disabled}
                    />
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <CurrencyInput
                        label="Valor Isonomia"
                        value={data.valor_isonomia}
                        onChange={e => handleChange('valor_isonomia', e.target.value)}
                        disabled={disabled}
                    />
                    <CurrencyInput
                        label="Valor PDV"
                        value={data.valor_pdv}
                        onChange={e => handleChange('valor_pdv', e.target.value)}
                        disabled={disabled}
                    />
                    <CurrencyInput
                        label="Valor FGTS"
                        value={data.valor_fgts}
                        onChange={e => handleChange('valor_fgts', e.target.value)}
                        disabled={disabled}
                    />
                </div>
            </div>

            {/* Payment Configuration */}
            <div className="space-y-4">
                <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Configuração de Pagamento</h5>
                <div className="grid grid-cols-3 gap-6">
                    <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Nº Parcelas</Label>
                        <Input
                            type="number"
                            min="1"
                            value={data.parcelas}
                            onChange={e => handleChange('parcelas', e.target.value)}
                            disabled={disabled}
                            className="bg-white border-slate-200 focus:bg-white transition-colors w-full"
                        />
                    </div>
                    <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Data 1ª Parcela</Label>
                        <Input
                            type="date"
                            value={data.data_primeira_parcela}
                            onChange={e => handleChange('data_primeira_parcela', e.target.value)}
                            disabled={disabled}
                            className="bg-white border-slate-200 focus:bg-white transition-colors text-xs w-full"
                        />
                    </div>
                    <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Data Última Parcela</Label>
                        <Input
                            type="date"
                            value={data.data_ultima_parcela}
                            onChange={e => handleChange('data_ultima_parcela', e.target.value)}
                            disabled={disabled}
                            className="bg-white border-slate-200 focus:bg-white transition-colors text-xs w-full"
                        />
                    </div>
                </div>
            </div>

            {/* Specific Period Values */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-4">
                <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Parcelas por Período</h5>
                <div className="grid grid-cols-3 gap-3">
                    <CurrencyInput
                        label="1º Período"
                        value={data.valor_parcela_primeiro_periodo}
                        onChange={e => handleChange('valor_parcela_primeiro_periodo', e.target.value)}
                        disabled={disabled}
                    />
                    <CurrencyInput
                        label="2º Período"
                        value={data.valor_parcela_segundo_periodo}
                        onChange={e => handleChange('valor_parcela_segundo_periodo', e.target.value)}
                        disabled={disabled}
                    />
                    <CurrencyInput
                        label="3º Período"
                        value={data.valor_parcela_terceiro_periodo}
                        onChange={e => handleChange('valor_parcela_terceiro_periodo', e.target.value)}
                        disabled={disabled}
                    />
                </div>
            </div>

            {/* Calculation Results Section */}
            <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 space-y-4">
                <div className="flex justify-between items-center">
                    <h5 className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Resumo da Proposta (Calculado Automaticamente)</h5>
                    <span className="text-[10px] font-medium text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">Automático</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <CurrencyInput
                        label="Valor À Vista"
                        value={data.valor_avista}
                        onChange={() => { }}
                        disabled={disabled}
                        isReadOnly={true}
                    />
                    <CurrencyInput
                        label="Proposta Final do Substituído"
                        value={data.valor_proposta_final}
                        onChange={() => { }}
                        disabled={disabled}
                        isReadOnly={true}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4 border-t border-blue-100 pt-4">
                    <CurrencyInput
                        label="Valor Parcelado Total"
                        value={data.valor_parcelado}
                        onChange={() => { }}
                        disabled={disabled}
                        isReadOnly={true}
                    />
                    <CurrencyInput
                        label="Valor de Cada Parcela"
                        value={data.valor_parcela}
                        onChange={() => { }}
                        disabled={disabled}
                        isReadOnly={true}
                    />
                </div>
            </div>
        </div>
    );
}
